import React, { useState } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import { FiAnchor } from 'react-icons/fi';
const fmt  = (n) => Number(n || 0).toLocaleString('en-US');
const fmtW = (n) => `${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })} MT`;

// Stable colours by sales type; unknowns fall back to the palette.
const TYPE_COLORS = { Domestic: '#6366F1', Oversea: '#14B8A6', Other: '#9CA3AF' };
const FALLBACK = ['#F59E0B', '#EF4444', '#A855F7', '#06B6D4', '#EC4899'];

const METRICS = [
  { key: 'total',  label: 'Count' },
  { key: 'weight', label: 'Weight' },
];

// Donut chart of Domestic vs Oversea split (by count or weight). Clicking a
// slice or legend row drills into that type's records.
const JobEntryDomesticOverseaChart = ({ data, loading, error, onDrillDown }) => {
  const [metric, setMetric] = useState('total');

  const rows = (data || []).map((r, i) => ({
    ...r,
    color: TYPE_COLORS[r.type] || FALLBACK[i % FALLBACK.length],
  }));
  const grandTotal = rows.reduce((s, r) => s + (r[metric] || 0), 0);
  const fmtMetric = (v) => (metric === 'weight' ? fmtW(v) : fmt(v));

  // Build cumulative arc offsets for an SVG donut (radius 60, circumference ~377).
  const R = 60;
  const C = 2 * Math.PI * R;
  let offset = 0;
  const segments = rows.map((r) => {
    const frac = grandTotal > 0 ? (r[metric] || 0) / grandTotal : 0;
    const seg = { ...r, frac, dash: frac * C, gap: C - frac * C, offset: -offset * C };
    offset += frac;
    return seg;
  });

  return (
    <Card padding="none" className="tw-border tw-border-gray-200 tw-shadow-sm tw-h-full">
      <div className="tw-px-5 tw-py-3 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-2">
        <div className="tw-flex tw-items-center tw-gap-2">
          <FiAnchor size={15} className="tw-text-teal-500" />
          <h3 className="tw-font-bold tw-text-gray-900 tw-text-sm tw-mb-0">Domestic vs Oversea</h3>
        </div>
        <div className="tw-inline-flex tw-rounded tw-border tw-border-gray-200 tw-overflow-hidden">
          {METRICS.map((opt, idx) => (
            <button type="button" key={opt.key} onClick={() => setMetric(opt.key)}
              className={`tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-cursor-pointer tw-border-0 ${idx > 0 ? 'tw-border-l tw-border-l-gray-200' : ''} ${metric === opt.key ? 'tw-bg-primary-600 tw-text-white' : 'tw-bg-white tw-text-gray-500 hover:tw-bg-gray-50'}`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="tw-flex tw-justify-center tw-py-10"><LoadingSpinner size="md" /></div>
      ) : error || rows.length === 0 || grandTotal === 0 ? (
        <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-10 tw-mb-0">No data for this period</p>
      ) : (
        <div className="tw-p-5 tw-flex tw-items-center tw-gap-6 tw-flex-wrap tw-justify-center">
          <div className="tw-relative tw-flex-shrink-0">
            <svg width="160" height="160" viewBox="0 0 160 160">
              <g transform="translate(80,80) rotate(-90)">
                <circle r={R} fill="none" stroke="#F1F5F9" strokeWidth="22" />
                {segments.map((s) => (
                  <circle key={s.type} r={R} fill="none" stroke={s.color} strokeWidth="22"
                    strokeDasharray={`${s.dash} ${s.gap}`} strokeDashoffset={s.offset}
                    onClick={() => onDrillDown && onDrillDown(`Type of Sales: ${s.type}`, { type_of_sales: s.type })}
                    style={onDrillDown ? { cursor: 'pointer' } : undefined}>
                    <title>{`${s.type}: ${fmtMetric(s[metric])} (${Math.round(s.frac * 100)}%)`}</title>
                  </circle>
                ))}
              </g>
            </svg>
            <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-pointer-events-none">
              <span className="tw-text-xl tw-font-bold tw-text-gray-900 tw-tabular-nums">{fmtMetric(grandTotal)}</span>
              <span className="tw-text-[10px] tw-text-gray-400 tw-uppercase">{metric === 'weight' ? 'Total Weight' : 'Total'}</span>
            </div>
          </div>

          <div className="tw-space-y-2 tw-min-w-[180px]">
            {segments.map((s) => (
              <div key={s.type}
                onClick={() => onDrillDown && onDrillDown(`Type of Sales: ${s.type}`, { type_of_sales: s.type })}
                className={`tw-flex tw-items-center tw-gap-2.5 tw-rounded tw-px-2 tw-py-1.5 tw-transition-colors ${onDrillDown ? 'tw-cursor-pointer hover:tw-bg-gray-50' : ''}`}>
                <span className="tw-w-3 tw-h-3 tw-rounded-sm tw-flex-shrink-0" style={{ background: s.color }} />
                <span className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-flex-1">{s.type}</span>
                <span className="tw-text-sm tw-font-bold tw-tabular-nums tw-text-gray-900">{fmtMetric(s[metric])}</span>
                <span className="tw-text-xs tw-text-gray-400 tw-w-10 tw-text-right">{Math.round(s.frac * 100)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

JobEntryDomesticOverseaChart.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.any,
  onDrillDown: PropTypes.func,
};
JobEntryDomesticOverseaChart.defaultProps = { data: [], loading: false, error: null, onDrillDown: null };

export default JobEntryDomesticOverseaChart;
